// Tunable game + protocol constants shared by the client and the server.
// Changing a value here changes both ends in lockstep on the next build.
/** Server simulation ticks per second. */
export const TICK_RATE = 20;
/** World-state broadcasts per second (must be <= TICK_RATE). */
export const SNAPSHOT_RATE = 10;
/** World bounds in pixels. The playfield is a large square. */
export const WORLD_WIDTH = 4000;
export const WORLD_HEIGHT = 4000;
/** Player movement speed in pixels/second. */
export const PLAYER_SPEED = 240;
/** Player body radius in pixels (used for clamping + rendering). */
export const PLAYER_RADIUS = 16;
// ---------------------------------------------------------------------------
// Combat + monsters (server-authoritative; clients never compute damage/HP).
// ---------------------------------------------------------------------------
export const PLAYER_MAX_HP = 5;
/** Respawn delay after a player dies (ms). 0 = immediate. */
export const PLAYER_RESPAWN_MS = 0;
export const MONSTER_MAX_HP = 20;
export const MONSTER_RADIUS = 18;
export const MONSTER_SPEED = 120; // slower than the player so they're kitable
/** How many monsters live around the centre at once. */
export const MONSTER_COUNT = 8;
/** Monsters spawn at a random point within this radius of the origin. */
export const MONSTER_SPAWN_RADIUS = 700;
/** A dead monster respawns this long after it's killed (ms). */
export const MONSTER_RESPAWN_MS = 3000;
/**
 * A chasing monster stops this far (centre-to-centre) from its target instead
 * of running on top of it — leaves roughly a one-unit gap between their bodies.
 */
export const MONSTER_STOP_DIST = 50;
/** Within this range of its aggro target, a monster deals melee damage. */
export const MONSTER_MELEE_RANGE = 58;
export const MONSTER_DAMAGE = 1;
/** A monster deals MONSTER_DAMAGE at most once per this interval (ms). */
export const MONSTER_DAMAGE_INTERVAL_MS = 1000;
/** Player must be within this range of its target to auto-fire. */
export const ATTACK_RANGE = 360;
/** Minimum gap between a player's fireballs (ms). */
export const ATTACK_INTERVAL_MS = 600;
export const FIREBALL_DAMAGE = 4; // ~5 hits to kill a MONSTER_MAX_HP monster
/** Visual travel speed of a fireball (px/s); cosmetic — damage is authoritative. */
export const FIREBALL_SPEED = 700;
/**
 * Fixed healing shrines. Static and known to both ends, so they are NOT
 * replicated — the client renders them from here and the server validates
 * proximity from here. Placed outside the monster spawn ring so you can retreat
 * to heal. Standing within HEAL_RADIUS and clicking heals to full HP.
 */
export const HEAL_NODES = [
    { x: 0, y: 0 }, // central safe-heal area
    { x: 1000, y: 0 },
    { x: -1000, y: 0 },
    { x: 0, y: 1000 },
    { x: 0, y: -1000 },
];
export const HEAL_RADIUS = 56;
/** Standing in a heal area restores 1 HP per this interval (~5s to full). */
export const HEAL_INTERVAL_MS = 1000;
/** Gold dropped when a monster dies. */
export const MONEY_PER_KILL = 5;
/** Tap a money pile within this range to collect it. */
export const MONEY_PICKUP_RANGE = 60;
/** A dropped money pile disappears after this long if not collected (ms). */
export const MONEY_TTL_MS = 30000;
/**
 * Area-of-interest radius: a client is only sent players within this many
 * pixels of itself. This is the mechanism that keeps bandwidth bounded at
 * 500 concurrent players — each client receives its neighbourhood, not the
 * entire world. See server/src/game.ts.
 */
export const AOI_RADIUS = 1400;
export const MAX_NAME_LEN = 16;
export const DEFAULT_NAME = "guest";
/** Email one-time-code (OTP) settings. */
export const OTP_LENGTH = 6;
export const OTP_TTL_MS = 5 * 60 * 1000;
export const OTP_MAX_ATTEMPTS = 5;
/** Minimum gap between code requests for one email (anti-abuse). */
export const OTP_RESEND_COOLDOWN_MS = 30 * 1000;
/** How often the server flushes dirty player positions to D1 (ms). */
export const PERSIST_INTERVAL_MS = 10 * 1000;
/** How long a kicked player's token is barred from reconnecting (ms). Shared so
 *  the client can show an accurate rejoin countdown. */
export const KICK_BAN_MS = 5 * 1000;
/** Client reconnects if no server message arrives within this window (ms). */
export const HEARTBEAT_TIMEOUT_MS = 15 * 1000;
/** Client ping cadence (ms). */
export const PING_INTERVAL_MS = 5 * 1000;
//# sourceMappingURL=constants.js.map